/**
 * JWT utility functions for token decoding and validation
 */

export interface DecodedJWT {
  sub?: string; // username
  jti?: string;
  cpid?: string; // company id
  cpname?: string; // company name
  dbid?: string; // database id
  ctx?: string; // context token
  ctxh?: string;
  sid?: string;
  auditLogsApi?: string; // audit logs API
  nbf?: number;
  exp?: number;
  iat?: number;
  iss?: string;
  aud?: string;
  use?: string;
}

/**
 * Decode a JWT token and return the payload
 * @param token - The JWT token to decode
 * @returns DecodedJWT object or null if decoding fails
 */
export const decodeJWT = (token: string): DecodedJWT | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => {
          return `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`;
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if a JWT token is expired
 * @param jwt - The decoded JWT object
 * @returns true if expired, false otherwise
 */
export const isJWTExpired = (jwt: DecodedJWT): boolean => {
  if (!jwt.exp) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  return currentTime > jwt.exp;
};

/**
 * Extract audit logs API endpoint from JWT token
 * @param token - The JWT token
 * @returns The audit logs API endpoint or null if not found
 */
export const getAuditLogsApiFromToken = (token: string): string | null => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.auditLogsApi) {
    return null;
  }

  // Normalize the API endpoint URL (remove trailing slash if present)
  let normalizedEndpoint = decoded.auditLogsApi;
  if (normalizedEndpoint.endsWith('/')) {
    normalizedEndpoint = normalizedEndpoint.slice(0, -1);
  }

  return normalizedEndpoint;
};
